import React from 'react';
import { Argument } from 'classnames';
import * as Flags from 'country-flag-icons/react/3x2';
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js/min';

import { useSelector } from 'hooks';
import { Country } from 'modules/refData/types/Countries';
import { useGetCountriesQuery } from 'modules/refData/service';
import { getSelectedLanguage } from 'modules/translations/selectors';

import PopoverLayout from './PopoverLayout';

interface CountrySelectProps {
  value: CountryCode;
  onChange: (country: Country) => void;
  className?: Argument;
  label: React.ReactNode;
  error?: React.ReactNode | boolean;
}

const SEARCHABLE_COUNTRY_KEYS: Array<keyof Country> = ['name', 'prefix', 'code', 'alpha3'];

const CountrySelect: React.FC<CountrySelectProps> = ({ value, onChange, className, label, error }) => {
  const language = useSelector(getSelectedLanguage);

  const [searchText, setSearchText] = React.useState<string>('');

  const { data: countries, isLoading } = useGetCountriesQuery({ language });

  const filteredCountries = React.useMemo(() => {
    if (countries) {
      if (searchText) {
        const searchNumberText = searchText.startsWith('+') ? searchText.substring(1) : searchText;
        const lowercasedSearchText = searchNumberText.toLowerCase();

        return countries.filter(country =>
          SEARCHABLE_COUNTRY_KEYS.some(key => {
            const countryValue = country[key];
            return String(countryValue).toLowerCase().includes(lowercasedSearchText);
          })
        );
      }
      return countries;
    }
    return [];
  }, [countries, searchText]);

  const callingCode = React.useMemo(() => value ? `+${getCountryCallingCode(value)}` : '', [value]);
  const Flag = React.useMemo(() => (value ? Flags[value as keyof typeof Flags] : undefined), [value]);

  const handleClick = React.useCallback((country: Country, close: () => void) => () => {
    onChange(country);
    setSearchText('');
    close();
  }, [onChange]);

  const handleSearch = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  return (
    <PopoverLayout
      className={className}
      error={error}
      countries={filteredCountries}
      handleClick={handleClick}
      handleSearch={handleSearch}
      isLoading={isLoading}
      label={label}
      searchText={searchText}
      setSearchText={setSearchText}
      value={value}
    >
      <div className="flex items-center h-full">
        {Flag && <Flag className="w-6 h-4 mr-2" />}
        <span data-testid="prefix-value">{callingCode}</span>
      </div>
    </PopoverLayout>
  );
};

export default CountrySelect;
